// Multi-page playback engine. Owns the voice-over <audio> element (passed in
// by the React component), the music/SFX Audio objects and the page clock.
//
// Page time model: each page lasts max(audioDuration, animDuration) + tail.
// While the page's voice-over is playing its currentTime is the clock; after
// it ends (or when the page has no audio) a wall-clock stopwatch (scaled by
// the playback rate) carries the remaining time. Seeking inside a page sets
// audio.currentTime; seeking past the audio pauses it at its end.
import { computeTiming, getPageAudio, pageAt } from "./layout"

const RATES = [0.75, 1, 1.25, 1.5, 2]
// one second of music crossing into the next, as the app does
const CROSSFADE_TIME = 1
const TICK_MS = 200

export default class PlayerEngine {
  constructor() {
    this.listeners = new Set()
    this.state = {
      ready: false,
      pageIndex: 0,
      elapsed: 0, // seconds inside the current page
      paused: true,
      completed: false,
      rate: 1,
      volume: 1,
      muted: false,
      seekId: 0, // bumps on every jump so views resync their animations
      transition: false, // the last page change asked for a fade
    }
    this.audio = null
    this.music = null
    this.musicSrc = null
    this.sfx = null
    this.pages = []
    this.timing = []
    this.total = 0
    this.autoPlay = true
    this.lastTick = 0
    this.timer = null
  }

  // ---- store ---------------------------------------------------------------
  subscribe = (listener) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
  getSnapshot = () => this.state
  set(patch) {
    this.state = { ...this.state, ...patch }
    this.listeners.forEach((listener) => listener(this.state))
  }

  // ---- setup ---------------------------------------------------------------
  attachAudio(element) {
    if (this.audio === element) return
    if (this.audio) this.detachAudio()
    this.audio = element
    if (!element) return
    element.preload = "auto"
    element.addEventListener("play", this.onAudioPlay)
    element.addEventListener("error", this.onAudioError)
    this.applyAudioSettings()
  }

  detachAudio() {
    if (!this.audio) return
    this.audio.removeEventListener("play", this.onAudioPlay)
    this.audio.removeEventListener("error", this.onAudioError)
    this.audio.pause()
    this.audio.removeAttribute("src")
    this.audio = null
  }

  onAudioPlay = () => {
    // Somebody else (e.g. the framework AudioPlayer) may pause us; nothing to do.
  }
  onAudioError = () => {
    // Broken audio file: the stopwatch will still finish the page.
  }

  load(product, { autoPlay = true, page } = {}) {
    this.product = product
    this.pages = product?.pages || []
    const { timing, total } = computeTiming(this.pages)
    this.timing = timing
    this.total = total
    this.autoPlay = autoPlay
    const index = Math.min(Math.max((page || 1) - 1, 0), Math.max(this.pages.length - 1, 0))
    this.set({ ready: this.pages.length > 0, completed: false })
    this.goToPage(index, { at: 0, play: autoPlay, transition: false })
  }

  destroy() {
    this.stopTimer()
    this.detachAudio()
    this.stopMusic()
    this.stopSfx()
    this.set({ paused: true })
  }

  // ---- navigation ----------------------------------------------------------
  goToPage(index, { at = 0, play, transition = true } = {}) {
    if (!this.pages.length) return
    index = Math.min(Math.max(index, 0), this.pages.length - 1)
    const shouldPlay = play ?? !this.state.paused
    const page = this.pages[index]
    const entry = this.timing[index]
    const position = Math.min(Math.max(at, 0), entry.duration)

    this.set({ pageIndex: index, elapsed: position, completed: false, transition, seekId: this.state.seekId + 1 })
    this.loadPageAudio(page, entry, position)
    this.loadPageMusic(page)
    this.loadPageSfx(page)
    if (shouldPlay) this.play()
    else this.pause()
  }

  next() {
    if (this.state.pageIndex + 1 >= this.pages.length) return this.finish()
    this.goToPage(this.state.pageIndex + 1)
  }

  prev() {
    if (this.state.elapsed > 3 || this.state.pageIndex === 0) return this.goToPage(this.state.pageIndex, { at: 0 })
    this.goToPage(this.state.pageIndex - 1)
  }

  replay() {
    this.goToPage(0, { at: 0, play: true })
  }

  finish() {
    this.stopTimer()
    this.audio?.pause()
    this.stopSfx()
    this.set({ paused: true, completed: true, elapsed: this.timing[this.state.pageIndex]?.duration || 0 })
  }

  /** Seek to a global position (seconds across all pages). */
  seekTo(position) {
    const index = pageAt(this.timing, position)
    const at = position - (this.timing[index]?.start || 0)
    if (index === this.state.pageIndex) return this.seekInPage(at)
    this.goToPage(index, { at, transition: false })
  }

  seekBy(delta) {
    this.seekTo(Math.min(Math.max(this.globalPosition() + delta, 0), Math.max(this.total - 0.5, 0)))
  }

  seekInPage(at) {
    const entry = this.timing[this.state.pageIndex]
    if (!entry) return
    const position = Math.min(Math.max(at, 0), entry.duration)
    this.set({ elapsed: position, completed: false, seekId: this.state.seekId + 1, transition: false })
    this.syncAudioTo(position, entry)
    this.lastTick = performance.now()
    if (this.state.paused) return
    if (this.audioShouldPlay(position, entry)) return this.tryPlayAudio()
    this.audio?.pause() // seeked past the voice-over: the stopwatch finishes the page
  }

  globalPosition() {
    return (this.timing[this.state.pageIndex]?.start || 0) + this.state.elapsed
  }

  // ---- transport -----------------------------------------------------------
  play() {
    if (this.state.completed) return this.replay()
    this.set({ paused: false })
    this.lastTick = performance.now()
    this.startTimer()
    const entry = this.timing[this.state.pageIndex]
    if (this.audioShouldPlay(this.state.elapsed, entry)) this.tryPlayAudio()
    this.music?.play().catch(() => {})
    this.sfx?.play().catch(() => {})
  }

  pause() {
    this.set({ paused: true })
    this.stopTimer()
    this.audio?.pause()
    this.music?.pause()
    this.sfx?.pause()
  }

  toggle() {
    if (this.state.paused) this.play()
    else this.pause()
  }

  cycleRate() {
    const i = RATES.indexOf(this.state.rate)
    this.setRate(RATES[(i + 1) % RATES.length])
  }

  setRate(rate) {
    this.set({ rate })
    this.applyAudioSettings()
  }

  setVolume(volume) {
    this.set({ volume: Math.min(Math.max(volume, 0), 1), muted: volume === 0 ? this.state.muted : false })
    this.applyAudioSettings()
  }

  setMuted(muted) {
    this.set({ muted })
    this.applyAudioSettings()
  }

  toggleMuted() {
    this.setMuted(!this.state.muted)
  }

  effectiveVolume() {
    return this.state.muted ? 0 : this.state.volume
  }

  applyAudioSettings() {
    const volume = this.effectiveVolume()
    if (this.audio) {
      this.audio.playbackRate = this.state.rate
      this.audio.volume = volume
    }
    // a crossfade owns the music's volume while it runs, and finishes on the
    // level this would have set
    if (this.music && !this.musicFade) this.music.volume = this.musicLevel()
    if (this.sfx) this.sfx.volume = volume * 0.6
  }

  /** How loud the music sits under the voice. */
  musicLevel() {
    return this.effectiveVolume() * 0.35
  }

  // ---- voice-over ----------------------------------------------------------
  loadPageAudio(page, entry, position) {
    const audio = this.audio
    if (!audio) return
    const src = getPageAudio(page)
    audio.pause()
    if (!src) {
      audio.removeAttribute("src")
      audio.load()
      return
    }
    if (audio.getAttribute("src") !== src) {
      audio.src = src
      audio.load()
    }
    this.applyAudioSettings()
    this.syncAudioTo(position, entry)
  }

  audioShouldPlay(position, entry) {
    return !!(this.audio && this.audio.getAttribute("src") && entry && position < entry.audioDuration)
  }

  syncAudioTo(position, entry) {
    const audio = this.audio
    if (!audio || !audio.getAttribute("src")) return
    const target = Math.min(position, Math.max(entry.audioDuration - 0.05, 0))
    const apply = () => {
      try {
        audio.currentTime = target
      } catch {
        /* not seekable yet */
      }
    }
    if (audio.readyState >= 1) return apply()
    audio.addEventListener("loadedmetadata", apply, { once: true })
  }

  tryPlayAudio() {
    const audio = this.audio
    if (!audio) return
    const promise = audio.play()
    if (!promise?.catch) return
    promise.catch((error) => {
      if (error?.name === "AbortError") return // superseded by a newer load
      // Autoplay policy refused: show the play button
      this.pause()
    })
  }

  // ---- music & sfx ---------------------------------------------------------
  /**
   * The music of a page, brought in over the music of the last one.
   *
   * Pages change while the music keeps going, so a cut is heard as a fault.
   * The app crosses one into the other over a second, which is what
   * CROSSFADE_TIME is in aldoo-books book_player/audio_player.lua.
   */
  loadPageMusic(page) {
    const src = page?.music
    if (!src) {
      // a page that says nothing about music keeps what is playing
      if (page?.usePrevMusic && this.music) return
      return this.fadeOutMusic()
    }
    if (this.musicSrc === src && this.music) return

    const previous = this.music
    const music = new Audio(src)
    music.loop = !!page.musicLoop
    music.preload = "auto"
    music.volume = 0
    this.music = music
    this.musicSrc = src
    if (!this.state.paused) music.play().catch(() => {})

    this.crossfade(previous, music)
  }

  /** Fade `from` out and `to` in, over CROSSFADE_TIME. */
  crossfade(from, to) {
    clearInterval(this.musicFade)
    const target = this.musicLevel()
    const started = performance.now()
    const fromLevel = from?.volume ?? 0

    this.musicFade = setInterval(() => {
      const done = Math.min(1, (performance.now() - started) / (CROSSFADE_TIME * 1000))
      if (to) to.volume = Math.min(1, target * done)
      if (from) from.volume = Math.max(0, fromLevel * (1 - done))
      if (done < 1) return

      clearInterval(this.musicFade)
      this.musicFade = null
      if (from) {
        from.pause()
        from.removeAttribute("src")
      }
      this.applyAudioSettings()
    }, 50)
  }

  /** Take the music out without putting anything in its place. */
  fadeOutMusic() {
    const previous = this.music
    if (!previous) return
    this.music = null
    this.musicSrc = null
    this.crossfade(previous, null)
  }

  stopMusic() {
    clearInterval(this.musicFade)
    this.musicFade = null
    if (!this.music) return
    this.music.pause()
    this.music.removeAttribute("src")
    this.music = null
    this.musicSrc = null
  }

  loadPageSfx(page) {
    this.stopSfx()
    if (!page?.sfx) return
    const sfx = new Audio(page.sfx)
    sfx.loop = !!page.sfxLoop
    sfx.preload = "auto"
    this.sfx = sfx
    this.applyAudioSettings()
  }

  stopSfx() {
    if (!this.sfx) return
    this.sfx.pause()
    this.sfx.removeAttribute("src")
    this.sfx = null
  }

  // ---- clock ---------------------------------------------------------------
  startTimer() {
    if (this.timer) return
    this.timer = setInterval(this.tick, TICK_MS)
  }

  stopTimer() {
    if (!this.timer) return
    clearInterval(this.timer)
    this.timer = null
  }

  tick = () => {
    if (this.state.paused) return
    const now = performance.now()
    const dt = ((now - this.lastTick) / 1000) * this.state.rate
    this.lastTick = now
    const entry = this.timing[this.state.pageIndex]
    if (!entry) return
    const audio = this.audio
    const audioActive =
      audio && audio.getAttribute("src") && !audio.ended && !audio.paused && audio.readyState >= 2
    let elapsed
    if (audioActive) elapsed = audio.currentTime
    else if (audio && audio.getAttribute("src") && !audio.ended && !audio.paused) elapsed = this.state.elapsed // buffering
    else elapsed = this.state.elapsed + dt

    if (elapsed >= entry.duration) {
      if (this.state.pageIndex + 1 >= this.pages.length) return this.finish()
      if (!this.autoPlay) return this.pause()
      return this.goToPage(this.state.pageIndex + 1, { at: 0, play: true, transition: true })
    }
    this.set({ elapsed })
  }
}

